export const ENDPOINT_API_DERIVATIVE_SERVICE_V2 = 'derivativeV2';
export const ENDPOINT_API_MODEL_DERIVATIVE_V2 = 'modelDerivativeV2'; // Forge
export const ENDPOINT_API_DERIVATIVE_STREAMING = 'streamingV2'; // SVF2

const FEDRAMP_STAGING_ENV = 'FedrampStaging2';
const FEDRAMP_PRODUCTION_ENV = 'FedrampProduction2';

/**
 * The Autodesk Partitions the endpoints cache is layered by, in addition to the region.
 * An Autodesk Region is always scoped by a Partition (e.g. Commercial `US` and GovCloud `US`
 * are isolated deployments), so the cache must be keyed by the partition-region pair.
 * Values align with the Regionalization Standard (RS-PARTITION-NAMES).
 * See https://autodesk.atlassian.net/wiki/spaces/CAS/pages/326730407/Regionalization+Standard
 */
export const PARTITIONS = Object.freeze({
  COMMERCIAL: 'COMMERCIAL',
  GOVCLOUD: 'GOVCLOUD',
  FEDRAMP_LEGACY: 'FEDRAMP_LEGACY'
});

const ENDPOINTS_LOCAL_STORAGE_KEY = 'Autodesk.Viewing.Private.endpoints';

// The maximum number of documents to cache in localStorage. Each document is stored as { urn, partition, region, cachedAt }.
// Not used when the region is pre-defined and provided in the constructor options.
const MAX_DOCS_CACHED = 1_000; // with ~0.12Kb on average, 1_000 documents result in ~120Kb on maximum
// lifetime of the cached document in localStorage, 24h
const TTL_MS = 24 * 60 * 60 * 1_000;

export const DEVELOPER_API_URLS = Object.freeze({
  local: "",
  dev: "https://api.aps.dev.autodesk.com",
  stg: "https://api.aps.stg.autodesk.com",
  prod: "https://api.aps.autodesk.com"
});

export const FEDRAMP_API_URLS = Object.freeze({
  stg: "https://api-stg.aps.afg.us.autodesk.com",
  prod: "https://api.aps.afg.us.autodesk.com"
});

const DS_ENDPOINTS_API = '/derivativeservice/v2/endpoints/:urn';
const VS_ENDPOINTS_API = '/modeldata/endpoints/:urn';

const location = typeof window !== "undefined" && window !== null && window.location || '';
const currentHost = location ? `${location.protocol}//${location.host}` : '';

var _env; //formerly avp.env
export function getEnv() {
  return _env;
}
export function setEnv(env) {
  _env = env;
  if (env.startsWith('MD20')) {
    console.warn(`env=${env} is deprecated and will be removed in a future release. Use Autodesk{env}2 instead, where env=Development, Staging, or Production`);
  }
}

/**
 * Discovers the endpoints for a document. Called from `Document.load`, i.e. once the document URN
 * is known and the access token is available — a user-defined endpoint provided to the
 * `Initializer` is picked up via {@link Endpoints.setGlobalEndpoint}.
 *
 * @param {string} documentId - The document URN, prefixed with `urn:`
 * @param {object} [options] - The `Document.load` options
 * @returns {Promise<void>}
 */
export async function loadEndpoints(documentId, options) {
  if (!Autodesk.Viewing.Private.useDSEndpoints()) {
    return;
  }

  const env = options?.env || getEnv();
  if (env === 'Local') {
    return;
  }

  // Without the `urn:` prefix documents are loaded from a local path.
  if (!documentId || documentId.indexOf('urn:') === -1) {
    return;
  }

  const urn = documentId.replace(/^urn:/i, '');

  const endpoints = Endpoints.getInstance(urn, options);
  const loadPromise = endpoints.load(urn);
  // register synchronously, so that a concurrent load of the same document (or of the same
  // partition-region, when it is configured) reuses this instance instead of fetching again
  Endpoints.setInstance(endpoints, urn);
  return loadPromise.then((result) => {
    if (!result) {
      Endpoints.removeInstance(endpoints, urn);
    }
  }).catch((err) => {
    Endpoints.removeInstance(endpoints, urn);
    throw err;
  });
}

/**
 * Returns the developer API URL used to create REST API requests
 *
 * @example `developer.api.autodesk.com`
 *
 * @param {string} [env] - The optional env to get the API URL for
 * @returns {string}
 */
export function getDeveloperApiUrl(env) {
  const __env = env || getEnv();

  if (__env === 'Local') return '';

  // backward compatibility
  if (/Local/.test(__env)) return currentHost;

  // fedramp
  if (__env === FEDRAMP_STAGING_ENV) return FEDRAMP_API_URLS.stg;
  if (__env === FEDRAMP_PRODUCTION_ENV) return FEDRAMP_API_URLS.prod;

  // developer.api
  if (/Prod/.test(__env)) return DEVELOPER_API_URLS.prod;
  if (/Dev/.test(__env)) return DEVELOPER_API_URLS.dev;
  return DEVELOPER_API_URLS.stg;
}

const DEPRECATED_API_URLS = Object.freeze({
  commercial: {
    development: 'https://developer-dev.api.autodesk.com',
    staging: 'https://developer-stg.api.autodesk.com',
    production: 'https://developer.api.autodesk.com'
  },
  fedramp: {
    staging: 'https://api-stg.afg.us.autodesk.com',
    production: 'https://api.afg.us.autodesk.com'
  }
});

/**
 * Returns true if the provided API URL is a now deprecated `developer.api` or similar.
 * Kept for validating the setups with such URLs as a custom endpoint.
 */
function isDeprecatedApiUrl(env, apiUrl) {
  const __env = env || getEnv();

  // fedramp
  if (__env === FEDRAMP_STAGING_ENV) return apiUrl == DEPRECATED_API_URLS.fedramp.staging;
  if (__env === FEDRAMP_PRODUCTION_ENV) return apiUrl === DEPRECATED_API_URLS.fedramp.production;

  // developer.api
  if (/Prod/.test(__env)) return apiUrl === DEPRECATED_API_URLS.commercial.production;
  if (/Dev/.test(__env)) return apiUrl === DEPRECATED_API_URLS.commercial.development;
  if (/Stag/.test(__env)) return apiUrl === DEPRECATED_API_URLS.commercial.staging;
  return false;
}

export class Endpoints {
  /**
   * The "user-defined" global endpoint
   *
   * @type {string|undefined}
   * @private
   */
  static _globalEndpoint;
  /**
   * Sets the user-defined global endpoint
   *
   * The endpoints are discovered at `Document.load`, prior to the manifest loading, since that is
   * the moment the document URN is known. Even though it's possible to configure `Document.load`
   * to use a custom endpoint, it is an inconvenient "non-standard" scenario. In order to help LMV
   * users to avoid that, the custom endpoint supplied at `Autodesk.Viewing.Initializer` is
   * remembered here (see `envinit.js#initializeServiceEndPoints`) and used as a fallback.
   *
   * @param {string} endpoint - The user-defined endpoint
   */
  static setGlobalEndpoint(endpoint) {
    Endpoints._globalEndpoint = endpoint;
  }

  /**
   * This will delete the existing Authorization header if the token is falsy,
   * similar to how LMV updates/deletes the header in `envinit.js`.
   */
  static refreshAccessTokenHeader(accessToken) {
    for (const endpoints of Endpoints.#documentEndpoints.values()) {
      if (accessToken) {
        endpoints.HTTP_REQUEST_HEADERS["Authorization"] = accessToken;
      } else {
        delete endpoints.HTTP_REQUEST_HEADERS["Authorization"];
      }
    }
  }
  /**
   * The "user-defined" endpoint, overrides any other endpoint
   *
   * It is set when provided with constructor's options.
   *
   * @type {string|undefined}
   */
  #customEndpoint;
  /**
   * The JSON object, which stores the response of the `/endpoints` API
   *
   * @type {object|undefined}
   */
  _endpoints;

  /**
   * @type {number|undefined}
   */
  _loadingTime;

  /**
   * @private
   */
  static #documentEndpoints = new Map();
  /**
   * @private
   */
  static #regionEndpoints = new Map();
  /**
   * @param {string} documentUrn - The document URN
   * @param {object} options - An optional object that allows configuring manually the API request parameters such as headers, endpoint etc.
   * @return {Endpoints}
   */
  static getInstance(documentUrn, options) {
    documentUrn = documentUrn?.replace(/^urn:/i, '');
    // region is a routing header (provided in `Document.load` headers or `Initializer` options);
    // partition is a top-level caching option only (never a header — it must not be sent on the wire).
    const region = (options?.headers?.region || options?.region)?.toUpperCase();
    // the default partition is derived from the env (FedRAMP envs => FEDRAMP_LEGACY, else COMMERCIAL)
    const partition = getPartition(options?.partition, options?.env);
    // in-memory endpoints are reused per partition-region pair (the same key the cache is layered by);
    // the region is normalized to its ISO code so it matches the localStorage cache key
    const key = region && getCacheKey(partition, normalizeRegion(region));

    const endpoints = (key && Endpoints.#regionEndpoints.get(key)) ??
    Endpoints.#documentEndpoints.get(documentUrn) ??
    new Endpoints(Object.assign({}, options, { region, partition }));

    if (key && !Endpoints.#regionEndpoints.has(key)) {
      // if `endpoints._region` is set, either the endpoints were already fetched or it is the same `region`
      // exception can only happen if the user provides a different `region` header in `Document.load`
      // for already cached document endpoints, which is a misconfiguration and should be logged as an error
      if (endpoints._region && endpoints._region !== normalizeRegion(region)) {
        throw new Error(`[Endpoints]: wrong region "${region}" configured for "${endpoints._region}" endpoints`);
      }
    }

    if (Endpoints.#documentEndpoints.has(documentUrn)) {
      endpoints.setEndpointAndApi(options?.endpoint, options?.api);
    }
    return endpoints;
  }

  /**
   * Registers an instance for reuse, by document URN and — once the region is known — by
   * partition-region. The region is only known upfront when it was configured by the caller;
   * otherwise it is discovered, and the instance is registered by URN alone.
   *
   * @param {Endpoints} endpoints - The instance to register
   * @param {string} documentUrn - The document URN, without the `urn:` prefix
   */
  static setInstance(endpoints, documentUrn) {
    const key = endpoints._region && getCacheKey(endpoints._partition, endpoints._region);
    if (key && !Endpoints.#regionEndpoints.has(key)) {
      Endpoints.#regionEndpoints.set(key, endpoints);
    }

    if (!Endpoints.#documentEndpoints.has(documentUrn)) {
      Endpoints.#documentEndpoints.set(documentUrn, endpoints);
    }
  }

  /**
   * Undoes {@link Endpoints.setInstance}, so that the next load of the document (or of the
   * partition-region) starts over instead of reusing an instance that has nothing to offer.
   *
   * Only the registrations pointing at this very instance are dropped: an instance shared by
   * several documents must stay registered for the ones that are still using it.
   *
   * @param {Endpoints} endpoints - The instance to unregister
   * @param {string} documentUrn - The document URN, without the `urn:` prefix
   */
  static removeInstance(endpoints, documentUrn) {
    const key = endpoints._region && getCacheKey(endpoints._partition, endpoints._region);
    if (key && Endpoints.#regionEndpoints.get(key) === endpoints) {
      Endpoints.#regionEndpoints.delete(key);
    }

    if (Endpoints.#documentEndpoints.get(documentUrn) === endpoints) {
      Endpoints.#documentEndpoints.delete(documentUrn);
    }
  }

  /**
   * @type {boolean} - true if the region is provided in the constructor options
   */
  isRegionConfigured = false;

  /**
   * @param {object} options
   * @param {string} [options.env] - The LMV env to get the API URL for; see `envinit.js#EnvironmentConfigurations` for a full list
   * of (mostly outdated) possible envs, recommended are `AutodeskStaging2` and `AutodeskProduction2`
   * @param {string} [options.api] - The LMV API flavor to get the endpoints for, defaults to `streamingV2`;
   * all others are not recommended, see `endpoints.js#_apis_data` for a legacy list of possible API flavors
   * @param {Object} [options.headers] - Optional headers to be passed when discovering endpoint (#fetchEndpoints)
   * @param {string} [options.endpoint] - The user-defined endpoint, overrides any other endpoint
   * @param {string} [options.region] - The region to get the endpoints for
   * @param {string} [options.partition] - The Autodesk Partition (see {@link PARTITIONS}) the region belongs to.
   * A top-level caching option only — it is never a header and is never sent to any backend.
   * Defaults based on env (FedRAMP envs => FEDRAMP_LEGACY, else COMMERCIAL)
   * @private
   */
  constructor(options) {
    this._env = options?.env || getEnv();
    this._api = options?.api || Autodesk.Viewing.endpoint.getApiFlavor() || ENDPOINT_API_DERIVATIVE_STREAMING;
    // defaults to the global endpoint
    this.#customEndpoint = options?.endpoint || Endpoints._globalEndpoint;
    this.HTTP_REQUEST_HEADERS = Object.assign(Autodesk.Viewing.endpoint.HTTP_REQUEST_HEADERS, options?.headers);

    this._partition = getPartition(options?.partition, this._env);
    if (options?.region) {
      this._region = normalizeRegion(options.region);
      this.isRegionConfigured = true;
    }

    this.#validateEndpointAndApi();
  }

  #validateEndpointAndApi() {
    if (this._api.startsWith(ENDPOINT_API_DERIVATIVE_STREAMING) && this.hasCustomEndpoint()) {
      if (this.#customEndpoint === getDeveloperApiUrl(this._env) || isDeprecatedApiUrl(this._env, this.#customEndpoint)) {
        throw new Error('developer.api endpoints do not support streaming APIs');
      }
      console.warn(`Using custom endpoints with streaming APIs might be troublesome, since streaming APIs are not region-independent.`);
    }
  }

  /**
   * Returns origin to use for constructing the endpoint discovery URL
   * @returns {string}
   */
  #getDiscoveryEndpoint() {
    if (!Autodesk.Viewing.Private.useVSCdnEndpoints()) {
      return getDeveloperApiUrl(this._env);
    }
    let envPrefix = 'dev';
    // commercial, GovCloud
    if (/Prod/.test(this._env)) envPrefix = 'prd';
    if (/Staging/.test(this._env)) envPrefix = 'stg';
    // legacy FedRAMP
    if (this._env === FEDRAMP_STAGING_ENV) envPrefix = 'sfr';
    if (this._env === FEDRAMP_PRODUCTION_ENV) envPrefix = 'pfr';
    const domain = this._partition === PARTITIONS.GOVCLOUD ? 'autodesk.us' : 'autodesk.com';
    const region = (this._region ?? 'usa').toLowerCase();
    return `https://vds.${envPrefix}.${region}.${domain}`;
  }

  #getHeaders(apiUrl, useVSCdn) {
    if (useVSCdn) {
      // no headers, see slack https://autodesk.slack.com/archives/CHTA6CQ07/p1787059190049489?thread_ts=1784833488.079099&cid=CHTA6CQ07
      // > It's intentionally unauthenticated — the response only contains CDN/APS host configuration (no user data),
      // > and adding auth would add latency to every viewer load. We do have throttling in place
      // > Also... you don't need to include the region header. It will be ignored even if it is included.
      return new Headers();
    }
    // legacy and modern APS endpoints require the Authorization header
    const headers = new Headers(this.HTTP_REQUEST_HEADERS);
    for (const key of headers.keys()) {
      // make sure no stale region header is sent, see DS API: https://autodesk.atlassian.net/wiki/x/aoaAHQ
      // NOTE: the region header is ignored for Forge Apps of internal Autodesk accounts,
      // but by default (i.e. for real customer accounts) the API fails if the region header is provided.
      // This is not documented anywhere, aka "should be expected", right.
      // Source (Slack): https://autodesk.slack.com/archives/C0FL5BJF7/p1766009088099519?thread_ts=1765768266.854089&cid=C0FL5BJF7
      if (key.toLowerCase() === 'region') {
        headers.delete(key);
      }
    }
    // modern APS global endpoint requires region header
    if (apiUrl === getDeveloperApiUrl(this._env)) {
      headers.append('region', this.isRegionConfigured ? this._region : 'USA');
    }
    return headers;
  }

  /**
   * @param {string} documentUrn - The document URN
   */
  async #fetchEndpoints(documentUrn) {

    const loadStart = performance.now();

    const endpoint = this.#customEndpoint || this.#getDiscoveryEndpoint();

    // Some known proxy setups use routing to apigee URL and "/derivativeservice/v2".
    // Let them know, that there are still some issues with VS_CDN_ENDPOINTS APIs.
    if (Autodesk.Viewing.Private.useVSCdnEndpoints() && this.hasCustomEndpoint()) {
      console.warn(`[Endpoints]: using a custom endpoint with enabled VS_CDN_ENDPOINTS is not supported yet.`);
    }
    // for same reason, fall back to DS endpoints API for now
    const useVSCdn = Autodesk.Viewing.Private.useVSCdnEndpoints() && !this.hasCustomEndpoint();

    const headers = this.#getHeaders(endpoint, useVSCdn);

    // Since historically (?) LMV and thus DS endpoints define paths as absolute (with trailing "/"),
    // we have to support base URLs with and w/o "/" at the end and not to omit additional "/.." segments,
    // so that, e.g., "http://foo.com/bar/" + "/buz" results in "http://foo.com/bar/buz"
    // and not in "http://foo.com/bar//buz" (simple concat) or "http://foo.com/buz" (`new URL()` spec way).
    // Note that we don't use `new URL()` because of that.
    // See https://developer.mozilla.org/en-US/docs/Web/API/URL/URL
    // and https://developer.mozilla.org/en-US/docs/Web/API/URL_API/Resolving_relative_references
    const baseUrl = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
    const endpointsApi = useVSCdn ? VS_ENDPOINTS_API : DS_ENDPOINTS_API;
    const url = `${baseUrl}${endpointsApi.replace(':urn', documentUrn)}`;
    const response = await fetch(url, { headers });

    if (!response.ok) {
      console.warn(`[Endpoints]: failed with ${response.status}, using the fallback endpoints`);
      Autodesk.Viewing.endpoint.setEndpointAndApi(null, this._api);
      return;
    }

    const result = await response.json();

    this._loadingTime = Number((performance.now() - loadStart).toFixed(0));

    return result;
  }

  /**
   * @param {string} documentUrn - The document URN
   * @returns {Promise<object|undefined>} - The promise, which resolves to the endpoints object,
   * or to `undefined` when there are none for this document and the caller did not configure a
   * region — the legacy endpoints are used in that case.
   * Rejects only on a misconfiguration: endpoints that contradict a configured region, or none
   * at all for a configured region.
   */
  async _getEndpoints(documentUrn) {
    const cachedEndpoints = this.hasCustomEndpoint() ? null :
    getFromLocalStorage({ env: this._env, partition: this._partition, region: this.isRegionConfigured && this._region, documentUrn });
    this._endpoints = cachedEndpoints || (await this.#fetchEndpoints(documentUrn));

    if (!this._endpoints) {
      if (this.isRegionConfigured) {
        // throw to catch misconfigurations earlier
        throw new Error(`[Endpoints]: no endpoints for document "${documentUrn}" in the configured region "${this._region}"`);
      }
      // The region was not configured - let it fall back to the legacy endpoints.
      return;
    }

    const region = normalizeRegion(getRegionFromHeaders(this._endpoints?.apigee?.headers));
    if (this.isRegionConfigured) {
      // throw to catch misconfigurations earlier
      if (region !== this._region) {
        throw new Error(`[Endpoints]: wrong region "${this._region}" configured for document "${documentUrn}"${region ? ` from "${region}" region` : ''}`);
      }
    } else {
      this._region = region;
    }

    this.#setRegionHeader();

    if (!cachedEndpoints && this.isRegionConfigured && !this.hasCustomEndpoint()) {
      updateLocalStorage(this._endpoints, { env: this._env, partition: this._partition, documentUrn, shouldCacheDocument: false });
    }

    return this._endpoints;
  }

  getLoadingTime() {
    return this._loadingTime ?? false;
  }

  #setRegionHeader() {
    // always set the region from the DS response, let backend to decide, if this is unnecessary
    // don't break the ref between `this.HTTP_REQUEST_HEADERS` and `Autodesk.Viewing.endpoint.HTTP_REQUEST_HEADERS`
    this.HTTP_REQUEST_HEADERS = Object.assign(Autodesk.Viewing.endpoint.HTTP_REQUEST_HEADERS, this._endpoints?.apigee.headers);
  }

  /**
   * @param {string} documentUrn - The document URN to load the endpoints for
   * @returns {Promise<boolean>} - The promise, which resolves to `true` if the endpoints were loaded,
   * or `false` if the feature is disabled or it's a local env
   */
  load(documentUrn) {
    // don't wait for endpoints if the feature is disabled or it's a local env
    if (!Autodesk.Viewing.Private.useDSEndpoints() || this._env === 'Local') {
      return Promise.resolve(false);
    }
    if (!this._loadPromise) {
      this._loadPromise = this._getEndpoints(documentUrn);
    }
    return this._loadPromise.then((endpoints) => {
      if (!endpoints) return false;

      Autodesk.Viewing.endpoint._endpoints = this;
      if (!this.isRegionConfigured && !this.hasCustomEndpoint()) {
        // add the document to the cached docs list, if the region is not configured
        updateLocalStorage(this._endpoints, { env: this._env, partition: this._partition, documentUrn, shouldCacheDocument: true });
      }
      return true;
    });
  }

  get endpoint() {
    if (!this._endpoints) {
      if (this._env !== 'Local') console.warn('No endpoints available, are they loaded?');
      return '';
    }
    const endpoint = this.#customEndpoint || this._endpoints.cdn.endpoint;
    // Since historically (?) LMV and thus DS endpoints define paths as absolute (with trailing "/"),
    // we have to support base URLs with and w/o "/" at the end and not to omit additional "/.." segments,
    // so that, e.g., "http://foo.com/bar/" + "/buz" results in "http://foo.com/bar/buz"
    // and not in "http://foo.com/bar//buz" (simple concat) or "http://foo.com/buz" (`new URL()` spec way).
    return endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
  }

  hasCustomEndpoint() {
    return !!this.#customEndpoint;
  }

  /**
   * Returns the endpoint plus the API used to create REST API requests
   *
   * @example `developer.api.autodesk.com/modelderivative/v2`
   *
   * @returns {string}
   */
  getEndpointAndApi() {
    if (!this.endpoint) return '';

    if (this._api.startsWith(ENDPOINT_API_DERIVATIVE_SERVICE_V2)) {
      if (this.endpoint === this._endpoints.apigee.endpoint || this.hasCustomEndpoint()) {
        return `${this.endpoint}${this._endpoints.apigee.dsAPIs.base}`;
      }
      return `${this.endpoint}${this._endpoints.cdn.dsAPIs.base}`;
    }
    if (this._api === ENDPOINT_API_MODEL_DERIVATIVE_V2) {
      if (this.endpoint === this._endpoints.apigee.endpoint || this.hasCustomEndpoint()) {
        return `${this.endpoint}${this._endpoints.apigee.mdAPIs.base}`;
      }
      return `${this.endpoint}${this._endpoints.cdn.mdAPIs.base}`;
    }
    return `${this.endpoint}${this._endpoints.cdn.streamAPIs.base}`;
  }

  setEndpointAndApi(endpoint, api) {
    if (endpoint && endpoint !== this.#customEndpoint) {
      this.#customEndpoint = endpoint;
    }
    if (api && api !== this._api) {
      this._api = api;
    }
    this.#validateEndpointAndApi();
  }

  /**
   * Returns the endpoint used to create REST API requests
   *
   * @example `developer.api.autodesk.com`
   *
   * @returns {string}
   */
  getApiEndpoint() {
    // ! this sets the `region` header (if any) within `initLoadContext` !
    this.#setRegionHeader();

    return this.endpoint;
  }

  /**
   * @returns {string}
   */
  getApiFlavor() {
    return this._api;
  }

  /**
   * Returns the default shared resource CDN location
   *
   * @example `vds.prd.irl.autodesk.com/cdn`
   *
   * @returns {string}
   */
  getCdnUrl() {
    if (!this.endpoint && !this._endpoints?.cdn) return '';

    return `${this._endpoints.cdn.endpoint}${this._endpoints.cdn.streamAPIs.cdn}`;
  }

  getCdnWebSocketEndpoint() {
    if (!this.endpoint && !this._endpoints?.cdn) return '';

    return `${this._endpoints.cdn.endpoint}${this._endpoints.cdn.streamAPIs.cdnWS}`;
  }

  /**
   * Returns a REST API string to request the manifest of the provided urn
   *
   * @example `developer.api.autodesk.com/modelderivative/v2/designdata/:urn/manifest`
   *
   * @param {string|null} endpoint - When provided is used instead of the globally set endpoint
   * @param {string} urn
   * @param {string} [api] - When provided is used instead of the globally set API flavor
   * @returns {string}
   */
  getManifestApi(endpoint, urn, api) {
    if (!this.endpoint) return '';

    const _api = api || this._api;
    const _endpoint = endpoint || this.endpoint;

    this.#setRegionHeader();

    if (_api.startsWith(ENDPOINT_API_DERIVATIVE_SERVICE_V2)) {
      if (_endpoint === this._endpoints.apigee.endpoint || this.hasCustomEndpoint()) {
        return `${_endpoint}${this._endpoints.apigee.dsAPIs.manifest}`;
      }
      return `${_endpoint}${this._endpoints.cdn.dsAPIs.manifest}`;
    }
    if (_api === ENDPOINT_API_MODEL_DERIVATIVE_V2) {
      if (_endpoint === this._endpoints.apigee.endpoint) {
        return `${_endpoint}${this._endpoints.apigee.mdAPIs.manifest}`;
      }
      // `cdn.mdAPIs` currently contains no manifest, even though it exists.
      // Workaround until it's fixed at the backend side
      const cdnBase = this._endpoints.cdn.mdAPIs.base;
      const apigeeBase = this._endpoints.apigee.mdAPIs.base;
      return `${_endpoint}${this._endpoints.apigee.mdAPIs.manifest.replace(apigeeBase, cdnBase)}`;
    }
    return `${_endpoint}${this._endpoints.cdn.streamAPIs.manifest}`;
  }

  /**
   * Returns a REST API string to request a derivative urn
   *
   * @example: `developer.api.autodesk.com/modelderivative/v2/designdata/:urn/manifest/:derivativeUrn`
   *
   * @param {string|null} endpoint - When provided is used instead of the globally set API endpoint
   * @param {string} derivativeUrn
   * @param {string} [api] - When provided is used instead of the globally set API flavor
   * @returns {string}
   */
  getItemApi(endpoint, derivativeUrn, api) {
    if (!this.endpoint) return '';

    const _api = api || this._api;
    const _endpoint = endpoint || this.endpoint;

    this.#setRegionHeader();

    if (_api.startsWith(ENDPOINT_API_DERIVATIVE_SERVICE_V2)) {
      if (_endpoint === this._endpoints.apigee.endpoint || this.hasCustomEndpoint()) {
        return `${_endpoint}${this._endpoints.apigee.dsAPIs.derivative}`;
      }
      return `${_endpoint}${this._endpoints.cdn.dsAPIs.derivative}`;
    }
    if (_api === ENDPOINT_API_MODEL_DERIVATIVE_V2) {
      const cdnBase = this._endpoints.cdn.mdAPIs.base;
      const apigeeBase = this._endpoints.apigee.mdAPIs.base;
      const derivative = _endpoint === this._endpoints.apigee.endpoint ?
      this._endpoints.cdn.mdAPIs.derivative.replace(cdnBase, apigeeBase) :
      this._endpoints.cdn.mdAPIs.derivative;

      // TODO: `manifest/:derivativeurn` and `manifest/:derivativeurn/singedcookies` are two different APIs,
      // but `endpoints` API currently returns `../singedcookies` as a `derivative` endpoint of the modelderivative service.
      // Workaround until it's fixed at the backend side
      return `${_endpoint}${derivative.replace(/(\/signedcookies)$/i, '')}`;
    }
    return `${_endpoint}${this._endpoints.cdn.streamAPIs.file}`;
  }

  /**
   * Returns a REST API string to request the thumbnail for a specific urn
   *
   * @example `developer.api.autodesk.com/modelderivative/v2/designdata/:urn/thumbnail`
   *
   * @param {string | null} endpoint - When provided is used instead of the globally set endpoint.
   * @param {string} urn
   * @param {string} [api] - When provided is used instead of the globally set API flavor
   * @returns {string}
   */
  getThumbnailApi(endpoint, urn, api) {
    if (!this.endpoint) return '';

    const _api = api || this._api;
    const _endpoint = endpoint || this.endpoint;

    this.#setRegionHeader();

    if (_api === ENDPOINT_API_MODEL_DERIVATIVE_V2) {
      if (_endpoint === this._endpoints.apigee.endpoint) {
        return `${_endpoint}${this._endpoints.apigee.mdAPIs.thumbnail}`;
      }
      // `cdn.mdAPIs` currently contains no `thumbnail`, even though it exists.
      // Workaround until it's fixed at the backend side
      const cdnBase = this._endpoints.cdn.mdAPIs.base;
      const apigeeBase = this._endpoints.apigee.mdAPIs.base;

      return `${_endpoint}${this._endpoints.apigee.mdAPIs.thumbnail.replace(apigeeBase, cdnBase)}`;
    }
    if (_api.startsWith(ENDPOINT_API_DERIVATIVE_SERVICE_V2) && (_endpoint === this._endpoints.apigee.endpoint || this.hasCustomEndpoint())) {
      return `${_endpoint}${this._endpoints.apigee.dsAPIs.thumbnail}`;
    }
    // streaming API has no thumbnail (same was with old endpoints)
    return `${_endpoint}${this._endpoints.cdn.dsAPIs.thumbnail}`;
  }

  /**
   * Returns a REST API string to make a property query
   *
   * @example `developer.api.autodesk.com/modelderivative/v2/designdata/:urn/metadata/:guid/properties:query`
   *
   * @param {string | null} endpoint - When provided is used instead of the globally set endpoint.
   * @param {string} urn
   * @param {string|null} api - When provided is used instead of the globally set API flavor
   * @param {string} guid
   * @returns {string}
   */
  getPropertyQueryApi(endpoint, urn, api, guid) {
    if (!this.endpoint) return '';

    const _endpoint = endpoint || this.endpoint;

    this.#setRegionHeader();

    if (_endpoint === this._endpoints.apigee.endpoint) {
      return `${_endpoint}${this._endpoints.apigee.mdAPIs.property}`;
    }

    // `cdn.mdAPIs` currently contains no `property`, even though it exists.
    // Workaround until it's fixed at the backend side
    const cdnBase = this._endpoints.cdn.mdAPIs.base;
    const apigeeBase = this._endpoints.apigee.mdAPIs.base;

    return `${_endpoint}${this._endpoints.apigee.mdAPIs.property.replace(apigeeBase, cdnBase)}`;
  }
}

function getRegionFromHeaders(headers) {
  if (headers) {
    for (const [key, value] of Object.entries(headers)) {
      if (key.toLowerCase() === 'region') {
        return value.toUpperCase();
      }
    }
  }
  return undefined;
}

/**
 * Deprecated DS region codes mapped to the ISO 3166-1 alpha-3 country codes approved by the
 * Regionalization Standard (RS-REGION-NAMES). Per the APS Locale system's supported-values table,
 * `US`/`EMEA`/`APAC` are legacy aliases ("don't use for new things") of `USA`/`IRL`/`AUS`; all other
 * supported regions (GBR, DEU, JPN, IND, CAN, ...) are already ISO codes and pass through unchanged.
 * See https://autodesk.atlassian.net/wiki/spaces/FRPA/pages/392873806/Setup+Locale+Region+Redirection
 */
const REGION_ALIASES = Object.freeze({
  US: 'USA',
  EMEA: 'IRL',
  APAC: 'AUS'
});

/**
 * Normalizes a region code to its ISO 3166-1 alpha-3 form for use in cache keys, mapping legacy
 * codes (US, EMEA, APAC) to the approved country codes (USA, IRL, AUS). Already-normalized and
 * unknown codes are returned uppercased and unchanged; falsy input is passed through.
 * @param {string} [region]
 * @returns {string|undefined}
 */
function normalizeRegion(region) {
  if (!region) return region;
  const __region = String(region).toUpperCase();
  return REGION_ALIASES[__region] ?? __region;
}

/**
 * The default partition for an env: FedRAMP envs map to `FEDRAMP_LEGACY`, everything else to `COMMERCIAL`.
 * This lets FedRAMP endpoints isolate via the partition segment of the cache key instead of a separate env key.
 * @param {string} [env] - The LMV environment
 * @returns {string} One of {@link PARTITIONS}
 */
function getDefaultPartition(env) {
  const __env = env || getEnv();
  if (__env === FEDRAMP_STAGING_ENV || __env === FEDRAMP_PRODUCTION_ENV) {
    return PARTITIONS.FEDRAMP_LEGACY;
  }
  return PARTITIONS.COMMERCIAL;
}

/**
 * Normalizes a caller-provided partition to a supported {@link PARTITIONS} value.
 * When none is provided (or an unknown value is given), falls back to the env's default partition.
 * @param {string} [partition] - The partition provided in options/headers
 * @param {string} [env] - The LMV environment, used to derive the default partition
 * @returns {string} One of {@link PARTITIONS}
 */
function getPartition(partition, env) {
  if (partition) {
    const __partition = String(partition).toUpperCase();
    if (Object.prototype.hasOwnProperty.call(PARTITIONS, __partition)) {
      return PARTITIONS[__partition];
    }
    console.warn(`[Endpoints]: unknown partition "${partition}", falling back to the default for env "${env || getEnv()}"`);
  }
  return getDefaultPartition(env);
}

/**
 * Builds the composite cache key layering a region under its partition, e.g. `COMMERCIAL-USA`.
 * Callers pass the region through {@link normalizeRegion} first, so the region segment is an ISO code.
 * @param {string} partition - The partition (see {@link PARTITIONS})
 * @param {string} region - The normalized (ISO 3166-1 alpha-3) region
 * @returns {string}
 */
function getCacheKey(partition, region) {
  return `${partition}-${region}`;
}

/**
 * Get a cache environment string (the deployment stage) mapped to the provided LMV environment:
 * - `/Staging/` => `staging` (includes `FedrampStaging2`)
 * - `/Development/` => `development`
 * - everything else => `production` (includes `FedrampProduction2`)
 *
 * The partition (COMMERCIAL/GOVCLOUD/FEDRAMP_LEGACY) is encoded in the cache key rather than the env,
 * so FedRAMP no longer needs its own env buckets — see {@link getDefaultPartition}.
 * @param {string} env - The LMV environment to map to a cache environment
 * @returns {string}
 */
function getCacheEnvironment(env) {
  const __env = env || getEnv();
  if (/Staging/.test(__env)) return 'staging';
  if (/Development/.test(__env)) return 'development';
  return 'production';
}

/**
 * Store endpoints in localStorage for a given partition-region and document URN.
 * @param {object} endpoints - The endpoints to cache in localStorage
 * @param {object} options - An object containing the options for caching the endpoints in localStorage
 * @param {string} options.env - The LMV environment to associate with the cached endpoints, defaults to production!
 * @param {string} options.partition - The Autodesk Partition (see {@link PARTITIONS}) the region belongs to
 * @param {string} [options.documentUrn] - The document URN to associate with the cached endpoints
 * @param {boolean} [options.shouldCacheDocument] - Whether to cache the document in localStorage (default: false);
 * the document is cached as { urn, partition, region, cachedAt } and the maximum number of documents cached is limited to MAX_DOCS_CACHED
 */
function updateLocalStorage(endpoints, options) {
  if (!Autodesk.Viewing.Private.useVSCdnEndpoints()) {
    return;
  }

  const { env, partition, documentUrn, shouldCacheDocument } = options;
  const region = normalizeRegion(getRegionFromHeaders(endpoints?.apigee?.headers));
  if (!region) {
    console.warn('No region found in endpoints headers, cannot cache endpoints in localStorage');
    return;
  }

  let cached = {};
  try {
    cached = JSON.parse(Autodesk.Viewing.Private.LocalStorage.getItem(ENDPOINTS_LOCAL_STORAGE_KEY)) || cached;
  } catch (e) {

    // ignore
  }
  const _env = getCacheEnvironment(env);
  cached[_env] = cached[_env] || {};
  cached[_env][getCacheKey(partition, region)] = endpoints;

  const dateNow = Date.now();

  const cachedDocuments = (cached.documents ? Object.entries(cached.documents).map(([urn, doc]) => ({ urn, ...doc })) : []
  // Remove expired documents from the cache
  ).filter((doc) => dateNow - doc.cachedAt < TTL_MS);

  if (shouldCacheDocument) {
    const cachedDocument = cachedDocuments.find((doc) => doc.urn === documentUrn);
    if (cachedDocument) {
      // refresh all mutable fields, not just the timestamp, so a record cached under stale
      // (or legacy, pre-normalization) partition/region values is corrected on re-cache
      cachedDocument.partition = partition;
      cachedDocument.region = region;
      cachedDocument.cachedAt = dateNow;
    } else {
      cachedDocuments.push({ urn: documentUrn, partition, region, cachedAt: dateNow });
    }
  }

  if (!cachedDocuments.length) {
    delete cached.documents;
  } else {
    cachedDocuments.sort((a, b) => b.cachedAt - a.cachedAt);
    cached.documents = cachedDocuments.slice(0, MAX_DOCS_CACHED).
    reduce((cache, doc) => {
      cache[doc.urn] = { partition: doc.partition, region: doc.region, cachedAt: doc.cachedAt };
      return cache;
    }, {});
  }

  Autodesk.Viewing.Private.LocalStorage.setItem(ENDPOINTS_LOCAL_STORAGE_KEY, JSON.stringify(cached));
}

/**
 * Get endpoints from localStorage for a given partition-region or document URN.
 * If both `region` and `documentUrn` are provided, the function will prioritize the region.
 * @param {object} options - An object containing the options for getting the endpoints from localStorage
 * @param {string} options.env - The LMV environment to get the endpoints for, defaults to production!
 * @param {string} options.partition - The Autodesk Partition (see {@link PARTITIONS}) the region belongs to.
 * @param {string} [options.region] - The region to get the endpoints for.
 * @param {string} [options.documentUrn] - The document URN to get the endpoints for.
 * @returns {object} The endpoints object for the given partition-region or document URN, or null if not found.
 */
function getFromLocalStorage(options) {
  if (!Autodesk.Viewing.Private.useVSCdnEndpoints()) {
    return null;
  }
  const { env, partition, region, documentUrn } = options;
  let cached = {};
  try {
    cached = JSON.parse(Autodesk.Viewing.Private.LocalStorage.getItem(ENDPOINTS_LOCAL_STORAGE_KEY)) || cached;
  } catch (e) {

    // ignore
  }const _env = getCacheEnvironment(env);
  if (region) {
    return cached[_env]?.[getCacheKey(partition, normalizeRegion(region))];
  }
  const cachedDocument = documentUrn && cached.documents?.[documentUrn];
  if (cachedDocument && Date.now() - cachedDocument.cachedAt < TTL_MS) {
    return cached[_env]?.[getCacheKey(cachedDocument.partition, normalizeRegion(cachedDocument.region))];
  }
  return null;
}